import axios, { AxiosError, AxiosInstance } from 'axios';
import type { LeadNotifyAdminResolved } from '../components/bot-constructor/botTypes';

const API_URL = process.env.REACT_APP_API_URL || '';
const TOKEN_KEY = 'bc_auth_token';
const API_KEY_MANUAL_KEY = 'bc_api_key_manual';
const API_KEY = (process.env.REACT_APP_API_KEY || '').trim();

function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function setStoredToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** Query/hash names used when redirecting from SMS cabinet: ?token=JWT or #token=JWT */
const URL_TOKEN_PARAMS = ['token', 'access_token', 'jwt'];

/**
 * Call once at app startup (before React render). Saves JWT from URL into localStorage
 * and strips it from the address bar (token must not remain in shared links).
 * Optional: ?logout=1 clears stored token.
 */
export function bootstrapAuthFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('logout') === '1' || url.searchParams.get('logout') === 'true') {
      setStoredToken(null);
      url.searchParams.delete('logout');
      const qs = url.searchParams.toString();
      window.history.replaceState({}, document.title, url.pathname + (qs ? `?${qs}` : '') + url.hash);
      return;
    }
    for (const name of URL_TOKEN_PARAMS) {
      const v = url.searchParams.get(name);
      if (v?.trim()) {
        setStoredToken(v.trim());
        url.searchParams.delete(name);
        const qs = url.searchParams.toString();
        window.history.replaceState({}, document.title, url.pathname + (qs ? `?${qs}` : '') + url.hash);
        return;
      }
    }
    const h = window.location.hash;
    if (h && h.length > 1) {
      const raw = h.startsWith('#') ? h.slice(1) : h;
      const hp = new URLSearchParams(raw);
      for (const name of URL_TOKEN_PARAMS) {
        const v = hp.get(name);
        if (v?.trim()) {
          setStoredToken(v.trim());
          hp.delete(name);
          const rest = hp.toString();
          window.history.replaceState({}, document.title, url.pathname + url.search + (rest ? `#${rest}` : ''));
          return;
        }
      }
    }
  } catch {
    /* ignore */
  }
}

/** Clear JWT (e.g. before redirect to SMS login). */
export function clearAuthToken(): void {
  setStoredToken(null);
}

type ApiDetail =
  | string
  | string[]
  | { msg?: string }[]
  | { code?: string; message?: string };

/** API error code from FastAPI `detail` (when backend returns `{"code": "..."}`). */
export function getApiErrorCode(err: unknown): string | undefined {
  const e = err as AxiosError<{ detail?: ApiDetail }>;
  const d = e.response?.data?.detail;
  if (d && typeof d === 'object' && !Array.isArray(d) && 'code' in d) {
    const code = (d as { code?: unknown }).code;
    return typeof code === 'string' && code.trim() ? code : undefined;
  }
  return undefined;
}

/** Human-readable API error (FastAPI `detail` or axios message). */
export function getApiErrorMessage(err: unknown): string {
  const e = err as AxiosError<{ detail?: ApiDetail }>;
  const d = e.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map((x) => (typeof x === 'object' && x && 'msg' in x ? (x as { msg: string }).msg : String(x)))
      .join(', ');
  }
  if (d && typeof d === 'object' && 'message' in d) {
    const m = (d as { message?: unknown }).message;
    if (typeof m === 'string' && m.trim()) return m;
  }
  if (d && typeof d === 'object' && !Array.isArray(d) && 'code' in d) {
    const c = (d as { code?: unknown }).code;
    if (typeof c === 'string' && c.trim()) return c;
  }
  return e.message || 'Request failed';
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL || '/api',
      timeout: 120000,
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
    this.api.interceptors.request.use((config) => {
      const t = getStoredToken();
      if (t) {
        config.headers.Authorization = `Bearer ${t}`;
      }
      let xKey = API_KEY;
      try {
        const manual = localStorage.getItem(API_KEY_MANUAL_KEY)?.trim();
        if (manual) xKey = manual;
      } catch {
        /* ignore */
      }
      if (xKey) {
        (config.headers as Record<string, string>)['X-API-Key'] = xKey;
      }
      return config;
    });
    this.api.interceptors.response.use(
      (r) => r,
      (err: AxiosError) => {
        if (err.response?.status === 401) {
          setStoredToken(null);
        }
        return Promise.reject(err);
      }
    );
  }

  async getBotConnections() {
    return this.api.get('/bot/connections');
  }

  async saveBotToken(token: string) {
    return this.api.post('/bot/connections', { token });
  }

  async deleteBotConnection(connectionId: string) {
    return this.api.delete(`/bot/connections/${connectionId}`);
  }

  async startBotConnection(connectionId: string) {
    return this.api.post(`/bot/connections/${connectionId}/start`);
  }

  async stopBotConnection(connectionId: string) {
    return this.api.post(`/bot/connections/${connectionId}/stop`);
  }

  async updateBotConnection(
    connectionId: string,
    data: {
      adminTelegramChatId?: number | null;
      adminTelegramChatIds?: number[] | null;
      leadWebhookUrl?: string | null;
      deleteMessagesEnabled?: boolean;
    }
  ) {
    return this.api.patch(`/bot/connections/${connectionId}`, data);
  }

  /** Telegram Bot API getChat for each id — username / names for lead admin UI */
  async resolveTelegramChats(connectionId: string, chatIds: number[]) {
    return this.api.post<{ admins: LeadNotifyAdminResolved[] }>(
      `/bot/connections/${connectionId}/resolve-chats`,
      { chatIds }
    );
  }

  async getBotChains(options?: { summary?: boolean }) {
    const params = options?.summary ? { summary: '1' } : undefined;
    return this.api.get('/bot/chains', { params });
  }

  async getBotChain(chainId: string) {
    return this.api.get(`/bot/chains/${chainId}`);
  }

  async uploadBotMedia(file: File, mediaType: string) {
    const form = new FormData();
    form.append('media_type', mediaType);
    form.append('file', file);
    return this.api.post<{
      id: string;
      url: string;
      fileName: string;
      contentType: string;
      sizeBytes: number;
    }>('/bot/media/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  private async withRetry503<T>(fn: () => Promise<T>, retries = 2): Promise<T> {
    for (let i = 0; i <= retries; i++) {
      try {
        return await fn();
      } catch (e: unknown) {
        const err = e as { response?: { status?: number } };
        if (err.response?.status === 503 && i < retries) {
          await new Promise((r) => setTimeout(r, 2000));
          continue;
        }
        throw e;
      }
    }
    throw new Error('Unreachable');
  }

  async createBotChain(data: {
    name: string;
    description?: string;
    triggerType: string;
    triggerValue: string;
    botConnectionId?: string | null;
    botConnectionIds?: string[];
    steps: unknown[];
    flowPositions?: Record<string, { x: number; y: number }>;
  }) {
    return this.withRetry503(() => this.api.post('/bot/chains', data));
  }

  async updateBotChain(
    id: string,
    data: {
      name?: string;
      description?: string;
      triggerType?: string;
      triggerValue?: string;
      botConnectionId?: string | null;
      botConnectionIds?: string[];
      steps?: unknown[];
      flowPositions?: Record<string, { x: number; y: number }> | null;
    }
  ) {
    return this.withRetry503(() => this.api.put(`/bot/chains/${id}`, data));
  }

  async deleteBotChain(id: string) {
    return this.api.delete(`/bot/chains/${id}`);
  }

  async getConnectionStats(connectionId: string) {
    // 404 — подключение не найдено для этого user_id или удалено; не бросаем, чтобы не засорять консоль
    return this.api.get(`/bot/connections/${connectionId}/stats`, {
      validateStatus: (status) => status === 200 || status === 404,
    });
  }

  /** Статистика по сценарию со всех привязанных ботов (те же поля, что у getConnectionStats). */
  async getChainStats(chainId: string) {
    return this.withRetry503(() =>
      this.api.get(`/bot/chains/${chainId}/stats`, {
        validateStatus: (status) => status === 200 || status === 404,
      })
    );
  }

  async getConnectionUsers(connectionId: string, params?: { name?: string; date_from?: string; date_to?: string }) {
    return this.api.get(`/bot/connections/${connectionId}/users`, { params });
  }

  async exportConnectionUsers(connectionId: string) {
    return this.api.get(`/bot/connections/${connectionId}/users/export`, {
      params: { format: 'csv' },
      responseType: 'blob',
    });
  }

  async getConnectionSubmissions(connectionId: string, limit = 100) {
    return this.api.get<{ submissions: Array<{
      id: string;
      bot_connection_id: string;
      chain_id?: string;
      telegram_user_id: number;
      telegram_username?: string;
      variables?: Record<string, unknown>;
      created_at?: string;
      createdAt?: string;
    }> }>(`/bot/connections/${connectionId}/submissions`, { params: { limit } });
  }

  async broadcastMessage(connectionId: string, text: string) {
    return this.api.post(`/bot/connections/${connectionId}/broadcast`, { text });
  }

  async getBroadcasts(params?: { page?: number; limit?: number }) { return this.api.get<{ broadcasts: Broadcast[]; page: number; limit: number; total: number }>('/broadcasts', { params }); }
  async getBroadcast(id: string) { return this.api.get<Broadcast>(`/broadcasts/${id}`); }
  async createBroadcast(data: BroadcastPayload & { action: 'draft' | 'send' | 'schedule' }) {
    return this.api.post<Broadcast>('/broadcasts', data);
  }
  async updateBroadcast(id: string, data: (BroadcastPayload & { action?: 'draft' | 'schedule' }) | { action: 'cancel' | 'send' }) {
    return this.api.patch<Broadcast>(`/broadcasts/${id}`, data);
  }

  async getStepUsers(chainId: string, stepId: string) {
    return this.api.get(`/bot/chains/${chainId}/step-users`, { params: { step_id: stepId } });
  }

  /** Current tenant id when `BOT_JWT_SECRET` is enabled (requires Bearer token). */
  async getAuthMe() {
    return this.api.get<{
      userId: number;
      name?: string;
      balance?: number | string;
      balanceLine?: string;
      currency?: string;
      messages?: number;
    }>('/auth/me');
  }

  /** Регистрирует заход в конструктор в БД (после getAuthMe). Ошибки можно игнорировать. */
  async recordConstructorVisit() {
    return this.api.post<{ ok: boolean }>('/auth/record_visit');
  }

  /** Флаги для UI без секретов (например, настроен ли BOT_WEBHOOK_BASE_URL). */
  async getPublicConfig() {
    return this.api.get<{ webhookBaseUrlConfigured: boolean }>('/public-config');
  }
}

export type BroadcastButton = { label: string; url: string };
export type BroadcastMediaItem = { id: string; type: 'image' | 'video' };
export type Broadcast = {
  id: string; name: string; messageText: string; imageMediaId?: string | null;
  mediaType?: 'image' | 'video' | null;
  mediaItems?: BroadcastMediaItem[];
  buttons: BroadcastButton[]; botConnectionIds: string[];
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'error' | 'cancelled';
  createdAt?: string; scheduledAt?: string | null; sentAt?: string | null;
};
export type BroadcastPayload = Omit<Broadcast, 'id' | 'status' | 'createdAt' | 'sentAt'>;

export default new ApiService();
